import { LocaleDetails } from 'shared/src/types/languages.type';
import { getTermsLinkLabel } from '@/lib/ui-content/app-compliance-strings';
import { FooterLogo } from '@/components/footer-logo';

export type FooterColumnLabels = {
  articles?: string;
  vision?: string;
  /** Links to /methodology ("How we pick"). Falls back to English where untranslated. */
  methodology?: string;
  contactUs?: string;
  careers?: string;
  /**
   * Links to /partners — brand-owner explainer, free AI-visibility check
   * (georacle.ai), and the partnership inquiry form. Falls back to English
   * where untranslated.
   */
  partners?: string;
  trademarkPolicy?: string;
  privacyPolicy?: string;
  changeRegion?: string;
};

type FooterHeadings = { explore: string; region: string; legal: string; sitemap: string };

/** Column headings and the sitemap link. Untranslated languages fall back to English. */
const FOOTER_HEADINGS: Record<string, FooterHeadings> = {
  en: { explore: 'Explore', region: 'Region', legal: 'Legal', sitemap: 'Sitemap' },
  fr: { explore: 'Explorer', region: 'Région', legal: 'Mentions légales', sitemap: 'Plan du site' },
  es: { explore: 'Explorar', region: 'Región', legal: 'Legal', sitemap: 'Mapa del sitio' },
  de: { explore: 'Entdecken', region: 'Region', legal: 'Rechtliches', sitemap: 'Sitemap' },
  it: { explore: 'Esplora', region: 'Regione', legal: 'Note legali', sitemap: 'Mappa del sito' },
  'pt-BR': { explore: 'Explorar', region: 'Região', legal: 'Jurídico', sitemap: 'Mapa do site' },
  'pt-PT': { explore: 'Explorar', region: 'Região', legal: 'Informação legal', sitemap: 'Mapa do site' },
  nl: { explore: 'Ontdekken', region: 'Regio', legal: 'Juridisch', sitemap: 'Sitemap' },
  pl: { explore: 'Odkrywaj', region: 'Region', legal: 'Informacje prawne', sitemap: 'Mapa strony' },
  sv: { explore: 'Utforska', region: 'Region', legal: 'Juridiskt', sitemap: 'Webbplatskarta' },
  ca: { explore: 'Explora', region: 'Regió', legal: 'Avís legal', sitemap: 'Mapa del lloc' },
  gl: { explore: 'Explorar', region: 'Rexión', legal: 'Aviso legal', sitemap: 'Mapa do sitio' },
  ja: { explore: '探す', region: '地域', legal: '法的情報', sitemap: 'サイトマップ' },
};

export function getFooterHeadings(languageCode: string): FooterHeadings {
  return FOOTER_HEADINGS[languageCode] ?? FOOTER_HEADINGS.en;
}

type Props = {
  lang: LocaleDetails;
  labels: FooterColumnLabels;
  /**
   * Renders the brand mark above the Region column. Off by default — see
   * FooterLogo for why it is opt-in rather than fleet-wide.
   */
  showMark?: boolean;
  /** False where the page already is home — see FooterLogo. */
  markLinksHome?: boolean;
};

/**
 * Explore | Region | Legal. Presentational only (no hooks), so the async server
 * Footer and the FooterClient render the exact same markup from one place —
 * keep it that way, or the two footers drift apart again.
 *
 * Layout notes, all of which look fine until you measure them:
 *  - Grid, not a flex row. Legal is wider than Explore, so a flex row with a
 *    fixed gap puts the middle column ~65px right of the footer's true centre —
 *    invisible until the mark sits on top of it. `1fr auto 1fr` centres the
 *    middle column regardless of what the side columns weigh.
 *  - Capped at 940px. Left to fill max-w-7xl the side columns drift to the
 *    edges and stop reading as a group.
 *  - Two columns on mobile, not one. Stacked single-file the three navs run
 *    ~10 links tall and push the legal line well past a phone fold; paired,
 *    Explore and Region share a row in desktop reading order and Legal spans
 *    underneath. The mark cannot ride along inside a half-width Region cell —
 *    it would sit on the footer's quarter-point — so it takes its own row.
 */
export function FooterColumns({ lang, labels, showMark, markLinksHome }: Props) {
  /**
   * The side columns are dropped so their headers land just under the mark's
   * midline. With no mark (product and guide pages), there is nothing to drop
   * under: the offset just leaves Region's header floating 48px above the other
   * two. Tie it to the mark rather than hardcoding it, so both footers stay
   * even wherever the mark is off.
   */
  const sideColumnOffset = showMark ? 'sm:pt-[48px]' : '';
  const headings = getFooterHeadings(lang.languageCode);

  return (
    <div
      className="grid grid-cols-2 justify-items-center items-start gap-y-10 gap-x-8
        sm:mx-auto sm:max-w-[940px] sm:grid-cols-[1fr_auto_1fr] sm:gap-x-[90px]"
    >
      {/* Mobile mark. Rendered separately from the desktop one below rather than
          moved by grid placement: keeping it inside the Region column on sm+ is
          what holds the 48px header offset the side columns are cut to. Only one
          of the two ever loads — a display:none lazy image never intersects. */}
      {showMark && (
        <div className="col-span-2 sm:hidden">
          <FooterLogo lang={lang} linkToHome={markLinksHome} />
        </div>
      )}

      <div className={`flex flex-col gap-4 items-center ${sideColumnOffset}`}>
        <span className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted">
          {headings.explore}
        </span>
        <nav className="flex flex-col gap-3 items-center" aria-label="Footer navigation">
          <a href={`/${lang.path}/articles`} className="text-muted transition-colors hover:text-ink">
            {labels.articles}
          </a>
          <a href={`/${lang.path}/vision`} className="text-muted transition-colors hover:text-ink">
            {labels.vision}
          </a>
          <a href={`/${lang.path}/methodology`} className="text-muted transition-colors hover:text-ink">
            {labels.methodology ?? 'How we pick'}
          </a>
          <a href={`/${lang.path}/contact-us`} className="text-muted transition-colors hover:text-ink">
            {labels.contactUs}
          </a>
          <a href={`/${lang.path}/careers`} className="text-muted transition-colors hover:text-ink">
            {labels.careers}
          </a>
          <a href={`/${lang.path}/partners`} className="text-muted transition-colors hover:text-ink">
            {labels.partners ?? 'Partners'}
          </a>
        </nav>
      </div>

      <div className="flex flex-col gap-4 items-center">
        {showMark && (
          <div className="max-sm:hidden">
            <FooterLogo lang={lang} linkToHome={markLinksHome} />
          </div>
        )}
        <span className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted">
          {headings.region}
        </span>
        <nav className="flex flex-col gap-3 items-center" aria-label="Region links">
          <a href={`/${lang.path}/region`} className="text-muted transition-colors hover:text-ink">
            {labels.changeRegion}
          </a>
          <a href={`/${lang.path}/sitemap`} className="text-muted transition-colors hover:text-ink">
            {headings.sitemap}
          </a>
        </nav>
      </div>

      {/* Odd column out: spans the pair so it centres rather than hanging left. */}
      <div className={`flex flex-col gap-4 items-center max-sm:col-span-2 ${sideColumnOffset}`}>
        <span className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted">
          {headings.legal}
        </span>
        <nav className="flex flex-col gap-3 items-center" aria-label="Legal links">
          <a href={`/${lang.path}/trademark-policy`} className="text-muted transition-colors hover:text-ink">
            {labels.trademarkPolicy}
          </a>
          <a href={`/${lang.path}/privacy`} className="text-muted transition-colors hover:text-ink">
            {labels.privacyPolicy}
          </a>
          <a href={`/${lang.path}/terms`} className="text-muted transition-colors hover:text-ink">
            {getTermsLinkLabel(lang.languageCode)}
          </a>
        </nav>
      </div>
    </div>
  );
}
